import { Autosuggest } from "@cloudscape-design/components";
import { FC, useState, useEffect } from "react";
import { algoliaClient, algoliaIndex } from "../../config/firebase";
import { capitalizeFirstLetter } from "../../utils/string";
import { useAuthContext } from "../../contexts/AuthContext";

export const typeToCollection: Record<string, string> = {
  batch: "batches",
  inventory: "inventory",
  invoice: "invoices",
};

export interface AlgoliaRecord {
  objectID: string;
  label: string;
  value: string;
  type: string;
  tenant: string;
  itemId: string;
  name: string;
  description: string;
  _highlightResult: {
    name: { value: string };
    description: { value: string };
    type: { value: string };
  };
}

interface SearchModalProps {
  onSelectRecord: (record: AlgoliaRecord) => void;
  placeholder?: string;
  header?: string;
  tenantId?: string;
  types?: string[];
  maxResults?: number;
}

const AlgoliaSearch: FC<SearchModalProps> = ({
  onSelectRecord,
  placeholder = "Search to navigate",
  types,
  maxResults,
}) => {
  const { tenant, tier } = useAuthContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<AlgoliaRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const performSearch = async () => {
      if (!searchTerm || !algoliaClient) return;

      setIsLoading(true);
      try {
        const filters: (string | string[])[] = [`tenant:${tenant}`];

        if (types && types.length > 0) {
          const typeFilter = types.map((type) => `type:"${type}"`).join(" OR ");
          filters.push(`(${typeFilter})`);
        }

        const response = (await algoliaClient.searchSingleIndex({
          indexName: algoliaIndex,
          searchParams: {
            query: searchTerm,
            hitsPerPage: maxResults ?? 10,
            filters: filters.join(" AND "),
          },
        })) as any;

        const hits: AlgoliaRecord[] = response.hits || [];
        setSearchResults(hits);
      } catch (err) {
        console.error("Algolia search error", err);
      } finally {
        setIsLoading(false);
      }
    };

    const timeout = setTimeout(() => {
      performSearch();
    }, 600);

    return () => clearTimeout(timeout);
  }, [searchTerm, tenant, types]);

  if (tier < 3) return null;

  return (
    <Autosuggest
      onChange={({ detail }) => setSearchTerm(detail.value)}
      value={searchTerm}
      options={searchResults.map((record) => {
        return {
          label: `${record.name} ${record.type === "batch" && !record.name.includes(record.description) ? `- ${record.description}` : ""}`,
          value: record.itemId,
          description: capitalizeFirstLetter(record.type),
        };
      })}
      loadingText={isLoading ? "Searching..." : undefined}
      ariaLabel="Search"
      placeholder={placeholder}
      empty="No matches found"
      onSelect={({ detail }) => {
        if (detail.selectedOption) {
          setSearchTerm("");
          const selectedRecord = searchResults.find(
            (record) => record.itemId === detail.selectedOption?.value
          );
          onSelectRecord(selectedRecord as AlgoliaRecord);
        }
      }}
    />
  );
};

export default AlgoliaSearch;
